import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { taskService } from '../services/taskService';
import { Project } from '../types/project';
import { Task, UpdateTask } from '../types/task';
import { User } from '../types/user';
import { projectService } from '../services/projectService';
import { userService } from '../services/userService';

interface ModifyTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  task: Task | null;
}

const ModifyTaskModal: React.FC<ModifyTaskModalProps> = ({ open, onClose, onSave, task }) => {
  const [taskData, setTaskData] = useState<UpdateTask>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchDropdownData = async () => {
        setLoading(true);
        try {
          const [projectData, userData] = await Promise.all([
            projectService.getProjects(),
            userService.getUsers(),
          ]);
          setProjects(projectData);
          setUsers(userData);
        } catch (err) {
          setError('Could not load required data.');
        } finally {
          setLoading(false);
        }
      };
      fetchDropdownData();
    }
  }, [open]);

  useEffect(() => {
    if (task) {
      setTaskData({
        title: task.title,
        project: task.project,
        assigned_to: task.assigned_to,
        status: task.status,
        description: task.description,
        due_date: task.due_date,
      });
    }
  }, [task]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setTaskData((prev: UpdateTask) => ({ ...prev, [name as string]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
        setTaskData((prev: UpdateTask) => ({ ...prev, due_date: format(date, 'yyyy-MM-dd') }));
    } else {
        setTaskData((prev: UpdateTask) => ({ ...prev, due_date: null }));
    }
  };

  const handleSubmit = async () => {
    if (!task) return;

    setIsSaving(true);
    setError(null);
    try {
      await taskService.updateTask(task.id, taskData);
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to update task:', err);
      setError(err.response?.data?.detail || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Modify Task</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <CircularProgress /> : (
            <>
              <TextField margin="normal" required fullWidth label="Task Title" name="title" value={taskData.title || ''} onChange={handleChange} autoFocus />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="project-select-label">Project</InputLabel>
                <Select labelId="project-select-label" name="project" value={taskData.project || ''} label="Project" onChange={handleChange}>
                  {projects.map((project: Project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="user-select-label">Assign To</InputLabel>
                <Select labelId="user-select-label" name="assigned_to" value={taskData.assigned_to || ''} label="Assign To" onChange={handleChange}>
                  {users.map((user: User) => (
                    <MenuItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField margin="normal" fullWidth label="Description" name="description" multiline rows={3} value={taskData.description || ''} onChange={handleChange} />
              <DatePicker
                label="Due Date"
                value={taskData.due_date ? new Date(taskData.due_date) : null}
                onChange={handleDateChange}
                sx={{ width: '100%', mt: 2, mb: 1 }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select labelId="status-select-label" name="status" value={taskData.status || 'TODO'} label="Status" onChange={handleChange}>
                  <MenuItem value="TODO">To Do</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="DONE">Done</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving || loading}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


export default ModifyTaskModal;